import { FRAIS_CAT } from '../constants/accounting';
import { dateVersIso, isoVersDate } from '../api/mappers';
import { dateIso, groupesDesTickets } from '../features/compta/TicketsA4.jsx';
import { dernieresParSorte, libelleAttestation } from './attestations';
import { depensesDuClasseur } from './classeur-depenses';
import { DEVISE, fmt } from './currency';
import { tr, trf } from './i18n';
import { paiementEtat } from './paiements-etat';
import { libelleMoyen, textePaye } from './paye-le';
import { auCentime, tvaDeLaDepense } from './vat';
import { tvaFactureFournisseur } from './costs';

/**
 * La TVA d'une pièce du dossier : une facture de fournisseur ou de
 * sous-traitant garde la TVA saisie (0 chez un fournisseur non assujetti),
 * comme dans la Compta et au serveur ; une dépense, celle de sa catégorie.
 */
const tvaDeLaPiece = (x, soc) =>
  x.genre === 'fournisseur' ? tvaFactureFournisseur(x, soc) : tvaDeLaDepense(x, FRAIS_CAT, soc);

/**
 * Les dépenses qui reviennent chaque mois — loyer, téléphone, assurances —
 * ont leur rubrique dans le dossier, séparée des tickets du quotidien.
 */
export const CATEGORIES_MENSUELLES = [
  'loyer',
  'location_bail',
  'communications',
  'assurance',
  'leasing',
  'energie',
  'interets',
  'bancaire',
];

const estMensuelle = (type) => CATEGORIES_MENSUELLES.includes(type);

/** Le numéro d'une pièce, sur trois chiffres : « 007 ». */
const numeroDePiece = (n) => String(n).padStart(3, '0');
// « tout » : les dépenses choisies une à une, de n'importe quelle année.
const deLAnnee = (date, annee) => annee === 'tout' || dateIso(date).startsWith(String(annee));

/**
 * Les années pour lesquelles un dossier peut se faire : celles des dépenses
 * et des factures de fournisseurs, la plus récente en tête. L'année en cours
 * y est toujours.
 *
 * @param {object} p pièces de la société, comme pour {@link depensesDuClasseur}
 * @param {Date} [maintenant] date de référence
 * @returns {string[]} les années
 */
export function anneesDuDossier(p, maintenant = new Date()) {
  const annees = new Set([String(maintenant.getFullYear())]);
  depensesDuClasseur(p).forEach(([, arr]) =>
    arr.forEach((x) => {
      const a = dateIso(x.date).slice(0, 4);
      if (a) annees.add(a);
    }),
  );
  return [...annees].sort().reverse();
}

/**
 * Ce que le dossier de l'année contient, rubrique par rubrique, avec le
 * nombre de pièces de chacune : l'écran l'affiche à côté des cases.
 *
 * @param {object} p pièces de la société
 * @param {object[]} [p.frais] dépenses
 * @param {object[]} [p.chantiers] chantiers
 * @param {object[]} [p.achatsJoints] factures jointes aux offres et factures
 * @param {object[]} [p.factures] factures clients
 * @param {object[]} [p.devis] offres
 * @param {object[]} [p.attestations] pièces de l'entreprise
 * @param {string|number} annee l'année du dossier
 * @returns {{tickets: object[], mensuelles: object[], achats: object[], attestations: object[],
 *   types: Array<[string, object[]]>}} les pièces de chaque rubrique
 */
export function contenuDuDossier(p, annee) {
  const types = depensesDuClasseur(p)
    .map(([type, arr]) => [type, arr.filter((x) => deLAnnee(x.date, annee))])
    .filter(([, arr]) => arr.length);
  const frais = (p.frais || []).filter((x) => typeof x.id === 'number' && deLAnnee(x.date, annee));
  const achats = [];
  types.forEach(([type, arr]) =>
    arr.filter((x) => x.origine !== 'frais').forEach((x) => achats.push({ ...x, type })),
  );
  achats.sort((a, b) => dateIso(a.date).localeCompare(dateIso(b.date)));
  const attestations = [
    ...dernieresParSorte(p.attestations),
    ...(p.attestations || []).filter((x) => x.categorie === 'attestation'),
  ];
  return {
    types,
    tickets: frais.filter((x) => !estMensuelle(x.type)),
    mensuelles: frais.filter((x) => estMensuelle(x.type)),
    achats,
    attestations,
  };
}

/**
 * La demande envoyée au serveur pour le PDF du dossier de l'année, déjà
 * écrite dans la langue de l'écran.
 *
 * @param {object} p pièces de la société (voir {@link contenuDuDossier})
 * @param {object} choix `{annee, resume, tickets, mensuelles, achats, attestations, titre?}` ;
 *   `annee: 'tout'` pour des dépenses choisies de n'importe quelle année ;
 *   `registre: false` sans la page du registre (une seule dépense)
 * @param {object} [soc] entreprise : nom, adresse, TVA, forme
 * @param {Date} [maintenant] date de référence
 * @returns {object} la demande (DossierAnneeDemande)
 */
export function demandeDuDossier(p, choix, soc, maintenant = new Date()) {
  const { annee } = choix;
  const contenu = contenuDuDossier(p, annee);
  const chf = (n) => DEVISE + ' ' + fmt(+n || 0);

  // Chaque pièce reçoit son numéro, par date : le même sur le registre, sur
  // le ticket et sur le bandeau de la facture. La fiduciaire retrouve une
  // dépense en lisant son numéro, sans feuilleter.
  const numerotees = [
    ...(choix.tickets ? contenu.tickets.map((x) => ({ x, genre: 'depense' })) : []),
    ...(choix.mensuelles ? contenu.mensuelles.map((x) => ({ x, genre: 'depense' })) : []),
    ...(choix.achats ? contenu.achats.map((x, rang) => ({ x, genre: 'achat', rang })) : []),
  ];
  // Rangées par type, comme le résumé — toute la sous-traitance ensemble,
  // puis le matériel… —, et par date dans chaque type.
  const rangDuType = new Map(contenu.types.map(([type], i) => [type, i]));
  const rang = (type) => rangDuType.get(type) ?? 999;
  numerotees.sort(
    (a, b) => rang(a.x.type) - rang(b.x.type) || dateIso(a.x.date).localeCompare(dateIso(b.x.date)),
  );
  const parType = new Map();
  numerotees.forEach(({ x }) => {
    const t = parType.get(x.type) || { n: 0, total: 0 };
    parType.set(x.type, { n: t.n + 1, total: t.total + (+x.montant || 0) });
  });
  const numeroDeLaDepense = new Map();
  const numeroDeLAchat = new Map();
  numerotees.forEach((e, i) => {
    e.numero = numeroDePiece(i + 1);
    if (e.genre === 'depense') numeroDeLaDepense.set(e.x.id, e.numero);
    else numeroDeLAchat.set(e.rang, e.numero);
  });

  const sommaire = [];
  const ajouteAuSommaire = (texte) => sommaire.push(sommaire.length + 1 + '. ' + texte);
  if (choix.resume) ajouteAuSommaire(tr('Résumé'));
  const avecRegistre = numerotees.length > 0 && choix.registre !== false;
  if (avecRegistre) ajouteAuSommaire(tr('Registre des pièces'));
  // Une dépense sans ticket n'a pas de page : elle est au registre. La
  // rubrique n'est annoncée que si au moins un justificatif y est posé.
  const avecPiece = (arr) => arr.some((x) => x.piece || x.justificatif || x.originalId);
  if (choix.tickets && avecPiece(contenu.tickets)) ajouteAuSommaire(tr('Tickets'));
  if (choix.mensuelles && avecPiece(contenu.mensuelles))
    ajouteAuSommaire(tr('Dépenses mensuelles'));
  if (choix.achats && contenu.achats.length)
    ajouteAuSommaire(
      tr(choix.magasin ? 'Factures fournisseurs' : 'Factures fournisseurs et sous-traitants'),
    );
  if (choix.attestations && contenu.attestations.length)
    ajouteAuSommaire(tr('Attestations de l’entreprise'));

  let resume = null;
  if (choix.resume) {
    // Les paiements à l'État (TVA, AVS, impôts) restent dans le dossier,
    // pièce par pièce, mais ne sont pas des dépenses : hors du résumé.
    const charges = contenu.types.filter(([type]) => !paiementEtat(type));
    const toutes = charges.flatMap(([type, arr]) => arr.map((x) => ({ ...x, type })));
    const total = toutes.reduce((s, x) => s + (+x.montant || 0), 0);
    resume = {
      entetes: [tr('Type'), tr('Pièces'), tr('Avec TVA')],
      lignes: charges.map(([type, arr]) => ({
        libelle:
          ((FRAIS_CAT[type] || {}).compte ? FRAIS_CAT[type].compte + '  ' : '') +
          tr((FRAIS_CAT[type] || {}).lib || type),
        pieces: String(arr.length),
        montant: chf(arr.reduce((s, x) => s + (+x.montant || 0), 0)),
      })),
      total: { libelle: tr('Total'), pieces: String(toutes.length), montant: chf(total) },
      tva: {
        libelle: tr('dont TVA récupérable'),
        pieces: '',
        montant: chf(auCentime(toutes.reduce((t, x) => t + tvaDeLaPiece(x, soc), 0))),
      },
      sommaire,
    };
  }

  const groupes = [
    ...(choix.tickets
      ? groupesDesTickets(contenu.tickets, { categorie: 'toutes', periode: String(annee) }, soc)
      : []),
    ...(choix.mensuelles
      ? groupesDesTickets(contenu.mensuelles, { categorie: 'toutes', periode: String(annee) }, soc)
      : []),
  ].map((g) => ({ ...g, numeros: g.ids.map((id) => numeroDeLaDepense.get(id) || '') }));
  const tickets = groupes.length
    ? {
        societe: soc?.nom || '',
        periode: annee === 'tout' ? '' : String(annee),
        devise: DEVISE,
        libelles: {
          pieces: tr('pièces'),
          page: tr('Page'),
          total: tr('Total'),
          tva: tr('dont TVA récupérable'),
          sansJustificatif: tr('Sans justificatif'),
          nombrePieces: tr('{0} pièce(s)'),
          especes: libelleMoyen('especes'),
          carte: libelleMoyen('carte'),
          banque: libelleMoyen('banque'),
          suite: tr('suite'),
          illisible: tr('Ce PDF ne s’ouvre pas'),
        },
        groupes,
        // Les dépenses sans ticket sont au registre, en orange : pas de case
        // vide sur les feuilles.
        seulementAvecPiece: true,
        // Les photos d'un même fournisseur ensemble, sous son nom, plusieurs
        // par feuille.
        parFournisseur: true,
      }
    : null;

  const pieces = [];
  const rangDeLAchat = new Map();
  if (choix.achats) {
    contenu.achats.forEach((x, rang) => {
      const cat = FRAIS_CAT[x.type] || {};
      rangDeLAchat.set(rang, pieces.length);
      pieces.push({
        numero: numeroDeLAchat.get(rang) || '',
        rubrique: tr(cat.lib || 'Matériel'),
        pieceId: x.pieceJointe?.id ?? null,
        titre: x.fournisseur || tr('Fournisseur'),
        soustitre: [x.date, cat.compte ? cat.compte + ' · ' + tr(cat.lib) : '']
          .filter(Boolean)
          .join(' · '),
        droite: [x.ou ? trf('Liée à {0}', x.ou) : '', chf(x.montant)].filter(Boolean).join(' · '),
        paye: textePaye(x.payeLe, x.payePar),
      });
    });
  }
  if (choix.attestations) {
    contenu.attestations.forEach((x) => {
      const fin = dateVersIso(x.date);
      pieces.push({
        rubrique: tr('Attestations de l’entreprise'),
        pieceId: x.id,
        titre:
          x.categorie === 'attestation'
            ? x.libelle || tr('Attestation')
            : tr(libelleAttestation(x.categorie, soc?.forme)),
        soustitre: fin ? trf('Valable jusqu’au {0}', isoVersDate(fin)) : '',
        droite: '',
        paye: '',
      });
    });
  }

  const registre = avecRegistre
    ? {
        titre: tr('Registre des pièces'),
        entetes: [
          tr('N°'),
          tr('Date'),
          tr('Fournisseur'),
          tr('Compte'),
          tr('Avec TVA'),
          tr('TVA'),
          tr('Pièce'),
        ],
        entrees: numerotees.map(({ x, genre, rang, numero }) => {
          const cat = FRAIS_CAT[x.type] || {};
          const t = parType.get(x.type);
          return {
            numero,
            groupe: [cat.compte, tr(cat.lib || x.type || '')].filter(Boolean).join(' · '),
            groupeResume: trf('{0} pièce(s)', t.n) + ' · ' + chf(t.total),
            date: x.date || '',
            fournisseur:
              String(x.fournisseur || '').split(' — ')[0] || x.libelle || tr(cat.lib || ''),
            compte: cat.compte || '',
            ttc: fmt(+x.montant || 0),
            tva: paiementEtat(x.type) ? '—' : fmt(tvaDeLaPiece(x, soc)),
            depenseId: genre === 'depense' ? x.id : null,
            piece: genre === 'achat' ? (rangDeLAchat.get(rang) ?? null) : null,
          };
        }),
      }
    : null;

  const adresse = [soc?.adr, soc?.npa].filter(Boolean).join(', ');
  return {
    societe: soc?.nom || '',
    titre: choix.titre || trf('Dépenses {0}', annee),
    soustitre: [
      adresse,
      soc?.tva ? tr('TVA') + ' ' + soc.tva : '',
      trf('établi le {0}', isoVersDate(maintenant.toISOString().slice(0, 10))),
    ]
      .filter(Boolean)
      .join(' · '),
    libelles: {
      page: tr('Page'),
      sansPiece: tr('Sans justificatif scanné'),
      suite: tr('suite'),
      caseTotal: tr('Total avec TVA'),
      caseTva: tr('TVA récupérable'),
      casePieces: tr('Pièces'),
      caseSans: tr('Sans justificatif'),
      avertSans: tr(
        '{0} dépenses sans justificatif : leur TVA est comptée, mais l’AFC ne l’admet que sur quittance. Elles sont dans le registre, en orange.',
      ),
      registreAide: tr(
        'Le numéro de chaque pièce est reporté sur son justificatif · toucher une ligne pour aller à sa page',
      ),
      pageCourte: tr('p.'),
      sansPieceCourt: tr('sans pièce'),
      avecPiece: tr('avec justificatif'),
    },
    resume,
    tickets,
    pieces,
    registre,
  };
}
